import React from 'react';
import { Renderer, RendererProps } from 'amis';
import Header from './components/Header';
import ResizablePanels from './components/ResizablePanels';
import DataTable from './components/DataTable';
import { AiChatPage } from '../AIChatbotPlugin/pages/AiChatPage';
import { RecipeData } from './types';
import { parseWorkflowOutput } from './utils/workflowOutputParser';
import type { MessageReceivedMetadata, AiChatPageRef } from '../AIChatbotPlugin/pages/AiChatPage/types';
import './styles/index.css';

/** 电气选型能力的 code，需与后台配置一致 */
const ELECTRICAL_SELECTION_CAPABILITY_CODE = 'electrical-selection';

/** 消息节流间隔（毫秒），避免流式消息疯狂触发刷新 */
const MESSAGE_THROTTLE_MS = 1000;

export interface AIElectricalSelectionPluginProps extends RendererProps {
  /** 覆盖默认的能力 code */
  readonly aiCapabilityCode?: string;
}

interface AIElectricalSelectionPluginState {
  tableId: string | null;
  collection: string | null;
  tableData: any[];
  changedCells: any[];
  isHistoryDrawerOpen: boolean;
  currentRecipe: RecipeData | null;
  refreshTrigger: number;
  refreshRecipeTrigger: number;
  exportExcelTrigger: number;
  exportCsvTrigger: number;
}

/** 单元格上下文（与 DataTable 中的 CellContext 保持一致） */
interface CellContext {
  row: number;
  col: number;
  cellValue: any;
  columnName: string;
  fieldName: string;
  rowId: string;
}

/** 浅比较两个 Record，判断值是否相同 */
const shallowEqualRecord = (
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key => a[key] === b[key]);
};

@Renderer({
  test: /\bagimx-ai-electrical-selection$/,
  name: 'agimx-ai-electrical-selection'
})
export default class AIElectricalSelectionPlugin extends React.Component<
  AIElectricalSelectionPluginProps,
  AIElectricalSelectionPluginState
> {
  /** 上次触发 refreshTrigger 的时间戳 */
  private _lastRefreshTime = 0;
  /** 延迟刷新 timer */
  private _pendingRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** ★ 缓存的 extraInputs，避免每次 render 产生新引用 */
  private _cachedExtraInputs: Record<string, unknown> = {};
  /** ★ AiChatPage 的 ref，用于程序化发送消息 */
  private _chatPageRef = React.createRef<AiChatPageRef>();
  /** ★ 记录上一次 _handleMessageReceived 处理过的 tableId，避免重复处理 */
  private _lastProcessedTableId: string | null = null;

  constructor(props: AIElectricalSelectionPluginProps) {
    super(props);

    this.state = {
      tableId: null,
      collection: null,
      tableData: [],
      changedCells: [],
      isHistoryDrawerOpen: false,
      currentRecipe: null,
      refreshTrigger: 0,
      refreshRecipeTrigger: 0,
      exportExcelTrigger: 0,
      exportCsvTrigger: 0,
    };

    this._handleNewRecipe = this._handleNewRecipe.bind(this);
    this._handleViewHistory = this._handleViewHistory.bind(this);
    this._handleTableDataUpdate = this._handleTableDataUpdate.bind(this);
    this._handleHistoryDrawerClose = this._handleHistoryDrawerClose.bind(this);
    this._handleSelectRecipe = this._handleSelectRecipe.bind(this);
    this._handleExportExcel = this._handleExportExcel.bind(this);
    this._handleExportCsv = this._handleExportCsv.bind(this);
    this._handleMessageReceived = this._handleMessageReceived.bind(this);
    this._handleAIQuestion = this._handleAIQuestion.bind(this);
  }

  componentDidMount(): void {
    console.log('AIElectricalSelectionPlugin mounted', this.props);
  }

  componentWillUnmount(): void {
    console.log('AIElectricalSelectionPlugin unmounting');
    if (this._pendingRefreshTimer) {
      clearTimeout(this._pendingRefreshTimer);
      this._pendingRefreshTimer = null;
    }
  }

  // ─── 头部操作 ─────────────────────

  _handleNewRecipe(): void {
    this._chatPageRef.current?.resetConversation();
    this._lastProcessedTableId = null;
    this.setState({
      tableId: null,
      collection: null,
      tableData: [],
      changedCells: [],
      currentRecipe: null,
    });
  }

  _handleViewHistory(): void {
    this.setState({
      isHistoryDrawerOpen: true,
      refreshRecipeTrigger: Date.now(),
    });
  }

  // ─── 表格数据 ─────────────────────

  _handleTableDataUpdate(data: any[]): void {
    this.setState({ tableData: data });
  }

  // ─── 历史记录 ─────────────────────

  _handleHistoryDrawerClose(): void {
    this.setState({ isHistoryDrawerOpen: false });
  }

  _handleSelectRecipe(recipe: RecipeData): void {
    this._lastProcessedTableId = recipe.tableId;
    this.setState({
      currentRecipe: recipe,
      tableId: recipe.tableId,
      isHistoryDrawerOpen: false,
    });
  }

  // ─── 导出 ─────────────────────

  _handleExportExcel(): void {
    this.setState({ exportExcelTrigger: Date.now() });
  }

  _handleExportCsv(): void {
    this.setState({ exportCsvTrigger: Date.now() });
  }

  // ─── ★ AI 单元格提问：DataTable 弹窗发送 → 通过 ref 发消息给 AI ─────────────────────

  _handleAIQuestion(question: string, cellContext?: CellContext): void {
    const chatPage = this._chatPageRef.current;
    if (!chatPage) {
      console.warn('[AIElectricalSelection] AiChatPage ref 不可用，无法发送消息');
      return;
    }

    const overrideInputs: Record<string, unknown> = {};
    if (cellContext) {
      overrideInputs.data_id = cellContext.rowId;
      overrideInputs.field = cellContext.fieldName;
    }

    chatPage.sendMessage(question, overrideInputs).catch((err) => {
      console.error('[AIElectricalSelection] 程序化发送 AI 消息失败:', err);
    });
  }

  // ─── 核心：AI 消息接收 → 解析 workflow 输出 → 更新表格 ─────────────────────

  _handleMessageReceived(message: string, metadata?: MessageReceivedMetadata): void {
    try {
      const output = parseWorkflowOutput(message, metadata?.workflowTrace);

      if (!output) {
        console.log('[AIElectricalSelection] AI 消息无表格操作数据，仅对话内容');
        return;
      }

      console.log('[AIElectricalSelection] 解析到 workflow 表格操作:', output);

      const now = Date.now();
      const elapsed = now - this._lastRefreshTime;

      const newTableId = output.tableId ?? this.state.tableId;
      const newCollection = output.collection ?? this.state.collection;
      const newChangedCells = (output.changedCells && output.changedCells.length > 0)
        ? [...output.changedCells]
        : this.state.changedCells;

      // ★ 关键修复：tableId 变化 vs 不变，走不同分支
      const isTableIdChanged = newTableId !== this.state.tableId;

      if (isTableIdChanged) {
        // ★ tableId 变了 → 只设 tableId/collection，不设 refreshTrigger
        // useTableData 的 Effect A 会自动 fetch，不需要额外触发
        this._lastProcessedTableId = newTableId;
        this._lastRefreshTime = now;
        this.setState({
          tableId: newTableId,
          collection: newCollection,
          changedCells: newChangedCells,
          // ★ 不设 refreshTrigger！Effect A 已经会处理
        });
        return;
      }

      // ★ tableId 没变 → 通过节流的 refreshTrigger 刷新
      // 先更新 changedCells（不受节流影响）
      if (newChangedCells !== this.state.changedCells) {
        this.setState({ changedCells: newChangedCells });
      }

      // ★ 如果已有 pending timer，不重复安排
      if (this._pendingRefreshTimer) {
        return;
      }

      if (elapsed >= MESSAGE_THROTTLE_MS) {
        this._lastRefreshTime = now;
        this.setState(prev => ({ refreshTrigger: prev.refreshTrigger + 1 }));
      } else {
        const delay = MESSAGE_THROTTLE_MS - elapsed;
        this._pendingRefreshTimer = setTimeout(() => {
          this._pendingRefreshTimer = null;
          this._lastRefreshTime = Date.now();
          this.setState(prev => ({ refreshTrigger: prev.refreshTrigger + 1 }));
        }, delay);
      }
    } catch (error) {
      console.error('[AIElectricalSelection] 解析 AI 消息失败:', error);
    }
  }

  // ─── ★ 构建传给 Dify 的额外参数（带缓存，避免引用不稳定） ─────────────────────

  private _buildExtraInputs(): Record<string, unknown> {
    const { tableId } = this.state;
    const nextInputs: Record<string, unknown> = {};

    if (tableId) {
      nextInputs.table_id = tableId;
    }
    // ★ 浅比较：只有值真正变化时才返回新对象引用
    if (!shallowEqualRecord(this._cachedExtraInputs, nextInputs)) {
      this._cachedExtraInputs = nextInputs;
    }

    return this._cachedExtraInputs;
  }

  // ─── Render ─────────────────────

  render(): React.ReactNode {
    const {
      tableId,
      collection,
      tableData,
      changedCells,
      isHistoryDrawerOpen,
      refreshTrigger,
      refreshRecipeTrigger,
      exportExcelTrigger,
      exportCsvTrigger,
    } = this.state;

    const capabilityCode = this.props.aiCapabilityCode ?? ELECTRICAL_SELECTION_CAPABILITY_CODE;
    const extraInputs = this._buildExtraInputs();

    return (
      <div className="es-root" style={{ height: this.props.height || '90vh' }}>
        <div className="es-shell">
          {/* ─── 左侧：AI 对话面板 ─── */}
          <div className="es-panel-left">
            <AiChatPage
              ref={this._chatPageRef}
              businessType={capabilityCode}
              defaultCapabilityCode={capabilityCode}
              capabilityDisabled
              sidebarMode="drawer"
              onMessageReceived={this._handleMessageReceived}
              onClickNewChat={this._handleNewRecipe}
              extraInputs={extraInputs}
            />
          </div>

          {/* ─── 右侧：数据表格面板 ─── */}
          <div className="es-panel-right">
            <div className="es-panel-header">
              <span className="es-panel-title"></span>
              <div className="es-panel-actions">
                {tableData.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="es-action-btn"
                      onClick={this._handleExportExcel}
                      title="导出 Excel"
                    >
                      <i className="fa fa-file-excel-o" />
                    </button>
                    <button
                      type="button"
                      className="es-action-btn"
                      onClick={this._handleExportCsv}
                      title="导出 CSV"
                    >
                      <i className="fa fa-file-text-o" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="es-panel-body">
              <DataTable
                tableId={tableId}
                collection={collection}
                data={tableData}
                changedCells={changedCells}
                refreshTrigger={refreshTrigger}
                onDataUpdate={this._handleTableDataUpdate}
                exportExcelTrigger={exportExcelTrigger}
                exportCsvTrigger={exportCsvTrigger}
                onAIQuestion={this._handleAIQuestion}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}